import { useEffect, useState } from 'react';
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import { useController } from 'react-hook-form';
import type { TextInputProps } from 'react-native';
import { TextInput, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Text } from '@/ui';

import { Tooltip } from '../tooltip';

interface InputMultiProps extends TextInputProps {
  label?: string;
  text?: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  style?: any;
  tooltipText?: string;
  disabled?: boolean;
  error?: string;
}

type TRule = Omit<
  RegisterOptions,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs'
>;

export type RuleType<T> = { [name in keyof T]: TRule };
export type InputControllerType<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  rules?: TRule;
};

interface ControlledInputProps<T extends FieldValues>
  extends InputMultiProps,
    InputControllerType<T> {}

export const InputMulti = ({
  label,
  text,
  onChangeText,
  placeholder,
  style,
  tooltipText,
  error,
  disabled = false,
}: InputMultiProps) => {
  const { styles, theme } = useStyles(stylesheet);

  const [internalText, setInternalText] = useState(text ?? '');

  useEffect(() => {
    setInternalText(text ?? '');
  }, [text]);

  const handleChange = (val: string) => {
    setInternalText(val);
    onChangeText(val);
  };

  return (
    <View style={{ gap: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {label ? <Text type="bodyRegular">{label}</Text> : null}
        {tooltipText ? <Tooltip text={tooltipText} /> : null}
      </View>

      <TextInput
        style={[styles.container, style]}
        value={internalText}
        multiline
        onChangeText={handleChange}
        placeholder={placeholder}
        placeholderTextColor={theme.colors.input.default.helperText}
        editable={!disabled}
      />

      {error ? (
        <View>
          <Text style={{ color: theme.colors.input.error.helperText }}>
            {error}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    width: '100%',
    borderColor: theme.colors.input.default.border,
    borderWidth: 1,
    borderRadius: 16,
    color: theme.colors.input.active.text,
    padding: 16,
    textAlignVertical: 'top',
  },
}));

// only used with react-hook-form
export function ControlledInputMulti<T extends FieldValues>(
  props: ControlledInputProps<T>
) {
  const { name, control, rules, ...inputProps } = props;

  const { field, fieldState } = useController({ control, name, rules });
  return (
    <InputMulti
      autoCapitalize="none"
      {...inputProps}
      onChangeText={(val: string) => {
        field.onChange(val);
        inputProps.onChangeText?.(val);
      }}
      text={(field.value as string) ?? ''}
      error={fieldState.error?.message}
    />
  );
}
